export type EtsyMoney = {
  amount: number;
  divisor: number;
  currency_code: string;
};

export type EtsyListingImage = {
  rank: number;
  url_570xN: string;
  url_fullxfull: string;
  full_width: number;
  full_height: number;
  alt_text?: string | null;
};

export type EtsyListing = {
  listing_id: number;
  shop_id: number;
  title: string;
  description?: string;
  state: "active";
  url: string;
  num_favorers: number;
  price: EtsyMoney;
  images?: EtsyListingImage[];
};

export type EtsyShop = {
  shop_id: number;
  shop_name: string;
  title: string;
  announcement: string;
  currency_code: string;
  url: string;
  icon_url_fullxfull?: string | null;
  image_url_760x100?: string | null;
  listing_active_count: number;
  review_count: number;
  review_average: number;
};

export interface EtsyDataSource {
  readonly kind: "mock" | "etsy-api";
  findShops(shopName: string): Promise<EtsyShop[]>;
  getShop(shopId: number): Promise<EtsyShop | null>;
  getFeaturedListings(shopId: number, limit: number): Promise<EtsyListing[]>;
  getActiveListings(shopId: number, limit: number): Promise<EtsyListing[]>;
  getListingsByIds(listingIds: number[]): Promise<EtsyListing[]>;
}

export type ImportedProduct = {
  id: string;
  title: string;
  description?: string;
  price: string;
  listingUrl: string;
  primaryImage: string;
  alternateImages: string[];
  imageWidth: number;
  imageHeight: number;
  favorites: number;
  featured: boolean;
};

export type ImportedShop = {
  source: EtsyDataSource["kind"];
  input: string;
  handle: string;
  id: string;
  name: string;
  title?: string;
  announcement?: string;
  shopUrl: string;
  icon?: string;
  banner?: string;
  activeListingCount: number;
  reviewCount: number;
  reviewAverage: number;
  products: ImportedProduct[];
};

export type ProductSelectionReason =
  | "Featured by the shop"
  | "Strong portrait image"
  | "High-resolution image"
  | "Concise display title"
  | "High customer interest";

export type ProductCardLayout = "overlay" | "catalogue";

export type ProductCardEdit = {
  productId: string;
  isNew?: boolean;
  image: string;
  title: string;
  price: string;
  layout: ProductCardLayout;
  zoom: number;
  offsetX: number;
  offsetY: number;
};

export type ProductImageTransform = Pick<ProductCardEdit, "zoom" | "offsetX" | "offsetY">;

export type SelectedProduct = {
  product: ImportedProduct;
  score: number;
  reasons: ProductSelectionReason[];
  cardLayout?: ProductCardLayout;
  imageTransform?: ProductImageTransform;
};

export type ProductSelection = {
  requested: number;
  selected: SelectedProduct[];
  rejected: Array<{ productId: string; reason: "duplicate-image" | "insufficient-image-data" | "selection-limit" }>;
};

export type CarouselVariant = "focus-carousel" | "vertical-focus-carousel" | "curved-focus-carousel";
export type ExperienceVariant = "meta" | "etsy";
export const MIN_CARD_DURATION_MS = 250;
export const MAX_CARD_DURATION_MS = 4_000;
export const DEFAULT_CARD_DURATION_MS = 1_000;
export const DEFAULT_CANVAS_BACKGROUND = "#181820";

export type { ExportFormat } from "./export-formats.js";

export type ImportShopRequest = { input: string };
export type ImportShopResponse = { shop: ImportedShop };
export type ImportShopError = { error: string; code: string };

export type CreateShopVideoRequest = { input: string; carouselVariant?: CarouselVariant; exportFormat?: import("./export-formats.js").ExportFormat; experience?: ExperienceVariant; productEdits?: ProductCardEdit[]; deletedProductIds?: string[]; cardDurationMs?: number; canvasBackground?: string };
export type CreateShopVideoResponse = {
  shop: ImportedShop;
  selection: ProductSelection;
  project: import("@hyperframes/react/browser").ProjectDocument;
  duration: number;
  previewHtml: string;
  exportHtml: string;
};
